import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  buildUiConsultationGroundingManifest,
  compressUiConsultationContext,
  type UiConsultationKind,
  type UiConsultationRunRequest,
} from '@triedge/assistant-context-engine';
import type {
  UiConsultationRunRecord,
  UiConsultationSessionRecord,
  UiConsultantStudioPageProps,
} from '../types';
import { SettingsBundleProvider, useSettingsBundle } from '../context/SettingsBundleContext';
import {
  ActionButton,
  DataTable,
  Field,
  KeyValueGrid,
  Notice,
  SelectInput,
  SettingsPanel,
  StatusPill,
  TextArea,
  TextInput,
  gridStyle,
  stackStyle,
} from '../components/ui';

const DEFAULT_CONSULTATION_KIND: UiConsultationKind = 'ui_interpretation';

const parseDelimitedLines = (
  value: string,
): Array<{ sourcePath: string; summary: string; excerpt?: string }> =>
  value
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [sourcePath, summary, excerpt] = entry.split('|').map((part) => part.trim());
      return {
        sourcePath: sourcePath || 'documentation/UNKNOWN.md',
        summary: summary || entry,
        excerpt: excerpt || undefined,
      };
    });

const parseJsonRecord = (value: string): Record<string, unknown> => {
  const trimmed = value.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const toneForStatus = (status: string): 'info' | 'success' | 'warning' | 'error' | 'neutral' => {
  if (status === 'completed' || status === 'ready') return 'success';
  if (status === 'degraded_completed' || status === 'capability_blocked' || status === 'disabled') return 'warning';
  if (status === 'failed') return 'error';
  if (status === 'running') return 'info';
  return 'neutral';
};

export const UiConsultantStudioPage = ({
  bundle,
  sessionId,
  uiConsultantRouteBasePath,
  uiConsultationContext,
}: UiConsultantStudioPageProps) => (
  <SettingsBundleProvider value={bundle}>
    <UiConsultantStudioPageContent
      sessionId={sessionId}
      uiConsultantRouteBasePath={uiConsultantRouteBasePath}
      uiConsultationContext={uiConsultationContext}
    />
  </SettingsBundleProvider>
);

const UiConsultantStudioPageContent = ({
  sessionId,
  uiConsultantRouteBasePath,
  uiConsultationContext,
}: {
  sessionId: string;
  uiConsultantRouteBasePath?: string;
  uiConsultationContext?: UiConsultantStudioPageProps['uiConsultationContext'];
}) => {
  const bundle = useSettingsBundle();
  const navigate = useNavigate();
  const resolvedUiConsultantRouteBasePath = uiConsultantRouteBasePath
    ?? `${bundle.routeManifest.routeBase}/integrations/ui-consultant`;
  const consultationRouteBase =
    uiConsultationContext?.routeBase ?? bundle.routeManifest.routeBase;
  const consultationSection = uiConsultationContext?.section ?? 'integrations';
  const defaultTargetRoute =
    uiConsultationContext?.defaultTargetRoute ?? '/super-administrator/control-plane/integrations';
  const defaultObjectTreeRoute =
    uiConsultationContext?.objectTreeRoute ?? defaultTargetRoute;
  const defaultObjectTreeSummary =
    uiConsultationContext?.objectTreeSummary
    ?? 'Settings bundle payload with integrations entry and consultation state.';
  const defaultRuntimeSummary =
    uiConsultationContext?.runtimeSummary
    ?? 'Settings-owned studio embedded under integrations.';
  const serviceStatusQuery = bundle.hooks.useGetUiConsultationServiceStatusQuery({});
  const sessionsQuery = bundle.hooks.useListUiConsultationSessionsQuery({});
  const sessionQuery = bundle.hooks.useGetUiConsultationSessionQuery(
    { sessionId },
    { skip: sessionId === 'new' },
  );
  const runsQuery = bundle.hooks.useListUiConsultationRunsQuery(
    { sessionId },
    { skip: sessionId === 'new' },
  );
  const [saveSession, saveSessionState] = bundle.hooks.useSaveUiConsultationSessionMutation();
  const [runConsultation, runConsultationState] = bundle.hooks.useRunUiConsultationMutation();

  const serviceStatus = serviceStatusQuery.data;
  const sessions = (sessionsQuery.data ?? []) as UiConsultationSessionRecord[];
  const currentSession = (sessionQuery.data ?? null) as UiConsultationSessionRecord | null;
  const runs = (runsQuery.data ?? []) as UiConsultationRunRecord[];

  const [title, setTitle] = useState('UI consultation session');
  const [consultationKind, setConsultationKind] = useState<UiConsultationKind>(DEFAULT_CONSULTATION_KIND);
  const [targetAppId, setTargetAppId] = useState('intellio-demo');
  const [targetRoute, setTargetRoute] = useState(defaultTargetRoute);
  const [targetPersona, setTargetPersona] = useState('school/staff');
  const [targetPackageName, setTargetPackageName] = useState('@triedge/staff-settings-ui');
  const [targetComponentName, setTargetComponentName] = useState('UiConsultantStudioPage');
  const [question, setQuestion] = useState('Interpret the current administration surface and recommend changes.');
  const [requirementsText, setRequirementsText] = useState(
    'documentation/INTERACTIVE-DECISION-SWARM-PROTOCOL.md | Consultation must stay governed through TailCodex.\n' +
      'documentation/tooling/TAILCODEX-OPERATOR-RUNTIME-TOOL.md | Operator runtime remains the membrane for costly actions.',
  );
  const [astSourcePath, setAstSourcePath] = useState('apps/intellio-demo/src/App.tsx');
  const [astRootKind, setAstRootKind] = useState('Route');
  const [astNodeLabels, setAstNodeLabels] = useState('SettingsShell\nIntegrationsSection\nUiConsultantStudioPage');
  const [objectTreeSummary, setObjectTreeSummary] = useState(defaultObjectTreeSummary);
  const [objectTreeJson, setObjectTreeJson] = useState(`{\n  "route": "${defaultObjectTreeRoute}",\n  "module": "settings",\n  "persona": "school/staff"\n}`);
  const [runtimeSummary, setRuntimeSummary] = useState(defaultRuntimeSummary);
  const [visibleTextBlocks, setVisibleTextBlocks] = useState('UI Consultant\nConsult with Opus\nLatest runs');
  const [testIds, setTestIds] = useState('ui-consultant-studio-root\nui-consultant-run-button\nui-consultant-save-button');
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [budgetSummary, setBudgetSummary] = useState<string | null>(null);

  useEffect(() => {
    if (!currentSession) return;
    setTitle(currentSession.title);
    setConsultationKind(currentSession.consultationKind);
    setTargetAppId(currentSession.targetAppId);
    setTargetRoute(currentSession.targetRoute);
    setTargetPersona(currentSession.targetPersona);
    setTargetPackageName(currentSession.targetPackageName);
    setTargetComponentName(currentSession.targetComponentName ?? 'UiConsultantStudioPage');
    setQuestion(currentSession.question);
  }, [currentSession]);

  const pending = saveSessionState.isLoading || runConsultationState.isLoading;

  const saveCurrentSession = async (): Promise<UiConsultationSessionRecord | null> => {
    try {
      const session = (await saveSession({
        id: sessionId === 'new' ? undefined : sessionId,
        title,
        consultationKind,
        targetAppId,
        targetRoute,
        targetPersona,
        targetPackageName,
        targetComponentName,
        question,
      }).unwrap()) as UiConsultationSessionRecord;
      setMessage('Consultation session saved.');
      setErrorMessage(null);
      if (sessionId === 'new') {
        navigate(`${resolvedUiConsultantRouteBasePath}/${session.id}`, { replace: true });
      } else {
        await sessionQuery.refetch?.();
      }
      await sessionsQuery.refetch?.();
      return session;
    } catch (error) {
      setMessage(null);
      setErrorMessage(error instanceof Error ? error.message : String(error ?? 'Unable to save consultation session.'));
      return null;
    }
  };

  const buildRequest = (session: UiConsultationSessionRecord): UiConsultationRunRequest => {
    const manifest = buildUiConsultationGroundingManifest({
      consultationKind,
      objective: `Interpret and critique ${targetRoute} for ${targetPersona}.`,
      question,
      target: {
        appId: targetAppId,
        route: targetRoute,
        persona: targetPersona,
        packageName: targetPackageName,
        componentName: targetComponentName || undefined,
        routeManifest: {
          routeBase: consultationRouteBase,
          section: consultationSection,
        },
        metadata: {
          sessionId: session.id,
        },
      },
      requirements: parseDelimitedLines(requirementsText).map((entry) => ({
        ...entry,
        priority: 'required' as const,
      })),
      astDigest: {
        sourcePath: astSourcePath,
        rootKind: astRootKind,
        nodeCount: astNodeLabels.split('\n').filter(Boolean).length,
        branchCount: 1,
        actionIds: [],
        controlIds: testIds.split('\n').map((entry) => entry.trim()).filter(Boolean),
        nodes: astNodeLabels
          .split('\n')
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((label) => ({
            kind: 'Element',
            label,
            props: [],
            testIds: [],
            children: [],
          })),
        metadata: {},
      },
      objectTreeDigest: {
        rootType: 'SettingsBundle',
        nodeCount: Object.keys(parseJsonRecord(objectTreeJson)).length,
        summary: objectTreeSummary,
        sample: parseJsonRecord(objectTreeJson),
        keys: Object.keys(parseJsonRecord(objectTreeJson)),
        actions: [],
        testIds: testIds.split('\n').map((entry) => entry.trim()).filter(Boolean),
        metadata: {},
      },
      runtimeDigest: {
        captureMode: 'snapshot',
        accessibilitySummary: runtimeSummary,
        visibleTextBlocks: visibleTextBlocks.split('\n').map((entry) => entry.trim()).filter(Boolean),
        layoutMarkers: [],
        actionLabels: [],
        testIds: testIds.split('\n').map((entry) => entry.trim()).filter(Boolean),
        objectTree: parseJsonRecord(objectTreeJson),
      },
      evidenceMap: [
        { id: 'requirements', section: 'requirements', label: 'Authority requirements' },
        { id: 'ast', section: 'ast_digest', label: 'Static structure digest', sourcePath: astSourcePath },
        { id: 'object-tree', section: 'object_tree', label: 'Deterministic object tree' },
        { id: 'runtime', section: 'runtime_digest', label: 'Runtime snapshot' },
      ],
    });
    const compressed = compressUiConsultationContext(manifest);
    setBudgetSummary(
      `${compressed.report.totalEstimatedTokens} est. tokens; trimmed: ${compressed.report.trimmedSections.join(', ') || 'none'}`,
    );
    return {
      consultationKind,
      target: manifest.target,
      question,
      grounding: compressed.manifest,
      budget: {
        softTokenTarget: 55000,
        hardTokenCap: 70000,
        sectionBudgets: {
          requirements: 0.15,
          targetIdentity: 0.05,
          astDigest: 0.3,
          objectTree: 0.25,
          runtimeDigest: 0.2,
          aceBlocks: 0.15,
          operatorQuestion: 0.05,
        },
      },
      artifactHints: {
        sessionId: session.id,
        requestedBy: 'staff-settings-ui',
        lane: 'intellio-demo',
      },
    };
  };

  const executeConsultation = async () => {
    const session = await saveCurrentSession();
    if (!session) return;
    try {
      const request = buildRequest(session);
      await runConsultation({
        session,
        request,
      }).unwrap();
      setMessage('Consultation run completed.');
      setErrorMessage(null);
      await runsQuery.refetch?.();
      await sessionsQuery.refetch?.();
      await serviceStatusQuery.refetch?.();
    } catch (error) {
      setMessage(null);
      setErrorMessage(error instanceof Error ? error.message : String(error ?? 'Consultation run failed.'));
    }
  };

  const latestRun = runs[0] ?? null;
  const capabilityNotice = serviceStatus?.availability && serviceStatus.availability !== 'ready';

  const runRows = useMemo<ReactNode[][]>(
    () =>
      runs.map((run) => [
        run.id,
        run.status,
        run.configuredModelLabel ?? 'Unknown model',
        run.updatedAt ?? 'Unknown',
        run.summary,
      ]),
    [runs],
  );

  return (
    <section
      data-testid="ui-consultant-studio-root"
      data-ui-consultant-visibility={serviceStatus?.visibility ?? 'hidden'}
      style={stackStyle(18)}
    >
      {message ? <Notice tone="success" title="UI consultation updated">{message}</Notice> : null}
      {errorMessage ? <Notice tone="error" title="UI consultation failed">{errorMessage}</Notice> : null}
      {capabilityNotice ? (
        <Notice tone="warning" title="Consultation lane is not fully available">
          {serviceStatus?.message}
        </Notice>
      ) : null}

      <SettingsPanel
        eyebrow="UI Consultant"
        title={title}
        description="TailCodex-governed Opus consultation studio for interpreting admin surfaces with deterministic grounding. This studio does not generate tenant-scoped reports or export artifacts."
        actions={(
          <>
            <ActionButton
              data-testid="ui-consultant-save-button"
              disabled={pending}
              onClick={() => void saveCurrentSession()}
              variant="secondary"
            >
              Save Session
            </ActionButton>
            <ActionButton
              data-testid="ui-consultant-run-button"
              disabled={pending}
              onClick={() => void executeConsultation()}
              variant="primary"
            >
              {runConsultationState.isLoading ? 'Running...' : 'Consult with Opus'}
            </ActionButton>
          </>
        )}
      >
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <StatusPill tone={toneForStatus(serviceStatus?.availability ?? 'disabled')}>
            {serviceStatus?.availability ?? 'disabled'}
          </StatusPill>
          <StatusPill tone={toneForStatus(latestRun?.status ?? 'draft')}>
            {latestRun?.status ?? 'draft'}
          </StatusPill>
          {budgetSummary ? <StatusPill tone="neutral">{budgetSummary}</StatusPill> : null}
        </div>

        <div style={gridStyle(2)}>
          <Field label="Session Title">
            <TextInput value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="Consultation Kind">
            <SelectInput
              value={consultationKind}
              onChange={(event) => setConsultationKind(event.target.value as UiConsultationKind)}
            >
              <option value="ui_interpretation">UI interpretation</option>
              <option value="ui_critique">UI critique</option>
              <option value="information_architecture_review">Information architecture review</option>
              <option value="route_design_review">Route design review</option>
            </SelectInput>
          </Field>
          <Field label="Target App">
            <TextInput value={targetAppId} onChange={(event) => setTargetAppId(event.target.value)} />
          </Field>
          <Field label="Target Route">
            <TextInput value={targetRoute} onChange={(event) => setTargetRoute(event.target.value)} />
          </Field>
          <Field label="Target Persona">
            <TextInput value={targetPersona} onChange={(event) => setTargetPersona(event.target.value)} />
          </Field>
          <Field label="Target Package">
            <TextInput value={targetPackageName} onChange={(event) => setTargetPackageName(event.target.value)} />
          </Field>
          <Field label="Component">
            <TextInput value={targetComponentName} onChange={(event) => setTargetComponentName(event.target.value)} />
          </Field>
          <Field label="AST Source Path">
            <TextInput value={astSourcePath} onChange={(event) => setAstSourcePath(event.target.value)} />
          </Field>
          <Field label="AST Root Kind">
            <TextInput value={astRootKind} onChange={(event) => setAstRootKind(event.target.value)} />
          </Field>
        </div>

        <Field label="Operator Question">
          <TextArea value={question} onChange={(event) => setQuestion(event.target.value)} />
        </Field>
        <Field label="Authority Requirements" helper="One per line: path | summary | optional excerpt">
          <TextArea value={requirementsText} onChange={(event) => setRequirementsText(event.target.value)} />
        </Field>
        <div style={gridStyle(2)}>
          <Field label="AST Labels" helper="One node label per line.">
            <TextArea value={astNodeLabels} onChange={(event) => setAstNodeLabels(event.target.value)} />
          </Field>
          <Field label="Runtime Visible Text" helper="One visible text block per line.">
            <TextArea value={visibleTextBlocks} onChange={(event) => setVisibleTextBlocks(event.target.value)} />
          </Field>
          <Field label="Object Tree Summary">
            <TextArea value={objectTreeSummary} onChange={(event) => setObjectTreeSummary(event.target.value)} />
          </Field>
          <Field label="Runtime Summary">
            <TextArea value={runtimeSummary} onChange={(event) => setRuntimeSummary(event.target.value)} />
          </Field>
        </div>
        <div style={gridStyle(2)}>
          <Field label="Object Tree JSON">
            <TextArea value={objectTreeJson} onChange={(event) => setObjectTreeJson(event.target.value)} />
          </Field>
          <Field label="data-testid Map" helper="One test id per line.">
            <TextArea value={testIds} onChange={(event) => setTestIds(event.target.value)} />
          </Field>
        </div>
      </SettingsPanel>

      <SettingsPanel
        eyebrow="Status"
        title="Consultation Lane"
        description="TailCodex service posture for the current consumer."
      >
        <KeyValueGrid
          items={[
            { label: 'Visibility', value: serviceStatus?.visibility ?? 'hidden' },
            { label: 'Availability', value: serviceStatus?.availability ?? 'disabled' },
            { label: 'Model', value: serviceStatus?.configuredModelLabel ?? 'Unknown' },
            { label: 'Message', value: serviceStatus?.message ?? 'No service status available.' },
          ]}
        />
      </SettingsPanel>

      <SettingsPanel
        eyebrow="History"
        title="Saved Sessions"
        description="Persisted session mirrors from the app consumer."
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <ActionButton
            onClick={() => navigate(`${resolvedUiConsultantRouteBasePath}/new`)}
            variant="secondary"
          >
            New Session
          </ActionButton>
          {sessions.map((session) => (
            <ActionButton
              key={session.id}
              onClick={() => navigate(`${resolvedUiConsultantRouteBasePath}/${session.id}`)}
              variant={session.id === sessionId ? 'primary' : 'secondary'}
            >
              {session.title}
            </ActionButton>
          ))}
        </div>
      </SettingsPanel>

      <SettingsPanel
        eyebrow="Runs"
        title="Consultation Runs"
        description="TailCodex artifact-backed run mirrors for this session."
      >
        <DataTable
          columns={['Run', 'Status', 'Model', 'Updated', 'Summary']}
          rows={runRows}
        />
      </SettingsPanel>

      {latestRun ? (
        <SettingsPanel
          eyebrow="Latest Result"
          title={latestRun.summary}
          description="Most recent consultation output for the current session."
        >
          <KeyValueGrid
            items={[
              { label: 'Run ID', value: latestRun.id },
              { label: 'Status', value: latestRun.status },
              { label: 'Model', value: latestRun.configuredModelLabel ?? 'Unknown' },
              { label: 'Artifact Dir', value: latestRun.artifactDir ?? 'Unavailable' },
              { label: 'Latest Pointer', value: latestRun.latestPointerPath ?? 'Unavailable' },
            ]}
          />
          {latestRun.response ? (
            <div style={gridStyle(2)}>
              <Field label="Strengths">
                <TextArea readOnly value={latestRun.response.strengths.join('\n')} />
              </Field>
              <Field label="Risks">
                <TextArea readOnly value={latestRun.response.failuresOrRisks.join('\n')} />
              </Field>
              <Field label="Interaction Recommendations">
                <TextArea readOnly value={latestRun.response.interactionRecommendations.join('\n')} />
              </Field>
              <Field label="Acceptance Checks">
                <TextArea readOnly value={latestRun.response.acceptanceChecks.join('\n')} />
              </Field>
            </div>
          ) : null}
        </SettingsPanel>
      ) : null}
    </section>
  );
};
